import { MiniGameStudent } from './mini-game.interface';

/**
 * High-level game mode the player picked at setup.
 *
 * - `ONE_VS_ONE` — exactly one student per side. Used for quick
 *   head-to-head rounds.
 * - `TEAM_VS_TEAM` — multiple students per side; turns rotate through
 *   each team's roster (see `BowlingParticipant.currentStudentIdx`).
 */
export enum BowlingMode {
  ONE_VS_ONE = 'one_vs_one',
  TEAM_VS_TEAM = 'team_vs_team'
}

/**
 * Lifecycle phase of a single bowling session, from team setup to game
 * over. The runtime currently only acts on `IDLE` (default) and
 * `FINISHED` (game over → summary screen). The intermediate values
 * (`AIMING`, `POWERING`, `ROLLING`, `RESOLVING`, `FRAME_BREAK`) are
 * reserved for richer phase tracking if we ever need to drive UI off
 * "what stage of a roll are we in" at the service layer rather than
 * the scene component (which currently owns its own
 * `rolling`/`holding`/`charging` flags).
 */
export enum BowlingPhase {
  IDLE = 'idle',
  AIMING = 'aiming',
  POWERING = 'powering',
  ROLLING = 'rolling',
  RESOLVING = 'resolving',
  FRAME_BREAK = 'frame_break',
  FINISHED = 'finished'
}

/**
 * Simplified bowling frame — each bowler gets exactly **one** roll per
 * frame in our scoring model. No second roll, no strike/spare bonuses,
 * no special 10th-frame handling. The total is the running sum of
 * `roll1` across all 10 frames (max 100).
 *
 * `null` means "not bowled yet" so the scorecard renders empty cells
 * for unplayed frames and the cumulative line stops at the last
 * played frame.
 */
export interface BowlingFrame {
  /** Pins knocked on this bowler's roll for this frame, 0–10, or null
   *  if the frame hasn't been played yet. */
  roll1: number | null;
  /** Running total through and including this frame, or null until
   *  this frame's roll lands. */
  cumulativeScore: number | null;
}

/**
 * One side of a bowling match — a team of one (1v1 mode) or many
 * students (team mode). Holds the side's identity, its roster, every
 * frame it's bowled, and the rotating cursor that picks which student
 * bowls next.
 */
export interface BowlingParticipant {
  /** Stable side identifier — A always renders left, B always right. */
  id: 'A' | 'B';
  /** Bilingual display name (e.g. "Blue Team" / "الفريق الأزرق").
   *  Auto-cycle defaults match the team colour palette below. */
  displayName: { en: string; ar: string };
  /** CSS hex string used for scoreboard tint, NEXT-UP card border, and
   *  in-scene CRT panel team-colour glow. */
  color: string;
  /** Roster — every student on this side. In team mode the rotation
   *  cursor walks through the array; in 1v1 mode there's only one. */
  students: MiniGameStudent[];
  /** Per-frame results — exactly 10 entries, indexed 0..9. */
  frames: BowlingFrame[];
  /** Index into `students` for whoever bowls next on this side. After
   *  this side finishes a frame, the cursor advances modulo the
   *  roster length so the next round starts with the next student. */
  currentStudentIdx: number;
  /** Cached running total — equals
   *  `frames[lastPlayedFrame].cumulativeScore`. Refreshed by
   *  `bowling-score.refreshTotals()` after each roll. */
  totalScore: number;
}

/**
 * Top-level state of one bowling session — owned by `MiniGameService`
 * and consumed read-only by every UI component (the scoreboard
 * overlay, the in-scene CRT panel, the NEXT-UP card, etc.).
 */
export interface BowlingState {
  /** 1v1 vs team-vs-team mode chosen at setup. */
  mode: BowlingMode;
  /** Always-two-element tuple of sides A and B. */
  participants: [BowlingParticipant, BowlingParticipant];
  /** Frame index 0..9; reaches 10 when the game ends (sentinel). */
  currentFrame: number;
  /** Which side bowls next — 0 = participants[0] (A), 1 = B. Each
   *  frame both sides bowl once, A first then B. */
  currentSide: 0 | 1;
  /** 10-bool mask for the next roll's pin rack — `true` = standing.
   *  Always `[true × 10]` in our model since each roll faces a fresh
   *  rack (no carryover-leave from a previous roll). Kept as a mask
   *  for forward-compatibility if we ever add multi-roll frames. */
  pinsStandingBefore: boolean[];
  /** Pin count from the most recent roll — drives the post-roll
   *  banner copy (e.g. "5 PINS"). */
  lastRollPins: number;
  /** Coarse session phase. Currently only `IDLE` / `FINISHED` are
   *  read by the runtime — see `BowlingPhase` above. */
  phase: BowlingPhase;
  /** Set when phase flips to `FINISHED`. `'tie'` means scores are
   *  equal; `null` while the game is in progress. */
  winnerSide: 0 | 1 | 'tie' | null;
}

/**
 * Factory for an empty (un-bowled) frame. Used by
 * `bowling-score.buildEmptyFrames()` to seed each side's `frames`
 * array at game start and by `MiniGameService` to reset between
 * sessions.
 */
export function createEmptyFrame(): BowlingFrame {
  return {
    roll1: null,
    cumulativeScore: null
  };
}
